import { makeObservable, action, observable, computed } from 'mobx';
import { INCOMPLETE, COMPLETE } from '$src/components/LocationsSelector/Locations/constants';
import { controlCenterApi } from '$src/api/control-center-api';
import { Notify } from '$src/utils/notify';

export default class LocationStatusStore {
  locations = null;
  loading = false;

  constructor(rootStore) {
    makeObservable(this, {
      locations: observable,
      loading: observable,
      unboardedLocations: computed,
      onboardedLocations: computed,
      unmonitoringLocations: computed,
      monitoringLocations: computed,
      eligibleLocations: computed,
      ineligibleLocations: computed,
      setLocations: action,
      fetchLocations: action,
      setLoading: action,
    });
    this.rootStore = rootStore;
  }

  fetchLocations = async (userId) => {
    try {
      this.setLoading(true);
      const locations = await controlCenterApi.fetchLocations(userId);
      // filter out any location the user does not own, we do not need to fetch status for these locations
      const locationOwnerIds = [...new Set(locations.addresses.filter((location) => location.isOwner).map((location) => location.ownerId))];
      const locationStatuses = await Promise.all(locationOwnerIds.map((ownerId) => controlCenterApi.fetchLocationsStatus(ownerId)));
      const allStatuses = Object.fromEntries(
        locationStatuses
          .flatMap((status) => status.locations || [])
          .filter((location) => location?.locationId)
          .map((location) => [location.locationId, location]),
      );

      const updatedLocationAddresses = locations?.addresses.map((location) => {
        const modeStatus = allStatuses[location.locationId];
        const DEFAULT_ADDRESS = 'Not found';
        const address1 = location?.address?.address1 ?? '';
        const address2 = location?.address?.address2 ?? '';
        const fullAddress = address1 && address2 ? `${address1}, ${address2}` : address1 || address2;

        return {
          ...location,
          ...(modeStatus || { eligible: false, monitoring: false, eligibilityReason: 'ENTITLEMENT_NOT_FOUND', monitoringReason: 'INELIGIBLE' }),
          currentUserRole: userId !== location.ownerId ? 'Manager' : 'Owner',
          fullAddress: fullAddress.trim() || DEFAULT_ADDRESS,
        };
      });

      this.setLocations(updatedLocationAddresses || []);
    } catch (e) {
      Notify.error('There was an error getting locations', e);
    } finally {
      this.setLoading(false);
    }
  };

  get unboardedLocations() {
    return this.locations?.filter((location) => location.onboardStatus === INCOMPLETE) || [];
  }

  get onboardedLocations() {
    return this.locations?.filter((location) => location.onboardStatus === COMPLETE) || [];
  }

  get unmonitoringLocations() {
    return this.locations?.filter((location) => !location.monitoring) || [];
  }

  get monitoringLocations() {
    return this.locations?.filter((location) => location.monitoring) || [];
  }

  get eligibleLocations() {
    return this.locations?.filter((location) => location.eligible) || [];
  }

  get ineligibleLocations() {
    return this.locations?.filter((location) => !location.eligible) || [];
  }

  setLocations(locations) {
    this.locations = locations;
  }

  setLoading(loading) {
    this.loading = loading;
  }
}
